import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import emmanuelImage from '../../../assets/team/emmanuel.jpg';
import CustomButton from '../../../components/common/CustomButton';
import { AppConfig } from '../../../core';

const teamMembers = [
  {
    name: 'Emmanuel Onyenanu',
    role: 'Founder',
    image: emmanuelImage,
    linkedin: 'https://www.linkedin.com/in/emmanuel-onyenanu-54747145/',
  },
];

const values = [
  {
    index: '01',
    title: 'Rescue',
    description:
      'Capture languages, traditions, and oral histories while living memory is still within reach.',
  },
  {
    index: '02',
    title: 'Connect',
    description:
      "Find the cousin across the world who knows the part of the story you've been missing.",
  },
  {
    index: '03',
    title: 'Celebrate',
    description: 'Honour the richness of African cultures with pride and joy.',
  },
];

export default function About() {
  const { palette, typography } = useTheme();
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        pt: { xs: 14, sm: 20 },
        pb: { xs: 8, sm: 12 },
      }}
    >
      <Helmet>
        <title>About us | {AppConfig.appName}</title>
        <meta name="description" content="Find out more about us" />
      </Helmet>
      {/* Section 1 — Hero */}
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 10 }} alignItems="flex-start">
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                letterSpacing: '.12em',
                fontWeight: 600,
              }}
            >
              Our Story
            </Typography>
            <Typography
              variant="h1"
              sx={{
                color: palette.primary.main,
                fontSize: {
                  xs: typography.h4.fontSize,
                  md: typography.h1.fontSize,
                },
                py: { xs: 2, md: 3 },
                lineHeight: 1.2,
                fontWeight: 800,
                fontFamily:
                  '"Playfair Display", "Georgia", "Times New Roman", serif !important',
              }}
            >
              Built before the stories disappear.
            </Typography>
            <Typography
              sx={{
                maxWidth: '65ch',
                fontSize: { xs: typography.body1.fontSize, md: '18px' },
                lineHeight: 1.8,
                color: 'text.primary',
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 600, color: palette.primary.main }}
              >{`${AppConfig.appName}`}</Box>{' '}
              was born from a simple observation: as African diaspora families
              build lives all over the world, vital stories, traditions, and
              connections risk being lost between generations. We built it
              specifically for us because nothing else was.
            </Typography>
            <Typography
              sx={{
                maxWidth: '65ch',
                fontSize: { xs: typography.body1.fontSize, md: '18px' },
                lineHeight: 1.8,
                color: 'text.primary',
                pt: 3,
              }}
            >
              We watched as grandparents took irreplaceable knowledge to their
              graves. We saw languages forgotten, village names lost, and
              traditions abandoned — not out of disrespect, but because there
              was no dedicated place to preserve them. We built{' '}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: palette.primary.main }}
              >{`${AppConfig.appName}`}</Box>{' '}
              to change that. To be the bridge between your past and your
              children&apos;s future.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <Box
              sx={{
                bgcolor: 'secondary.light',
                border: '1px solid',
                borderColor: 'secondary.main',
                borderRadius: 5,
                p: { xs: 4, md: 5 },
                mt: 8,
                width: '100%',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  letterSpacing: '.12em',
                  fontWeight: 600,
                  mb: 1.5,
                }}
              >
                Why it can't wait.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: palette.primary.main,
                  mb: 1.5,
                  lineHeight: 1.2,
                }}
              >
                Your roots, reimagined.
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  fontSize: '15px',
                }}
              >
                Every year, elders pass on. Languages go quiet. Village names
                are forgotten. LegacyTies exists because that loss is
                preventable and it starts with one family deciding to act.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      {/* Section 2 — Value Pillars */}
      <Box
        sx={{
          width: '100%',
          py: { xs: 6, md: 10 },
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {values.map((v) => (
              <Grid item xs={12} sm={6} md={4} key={v.title}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: { xs: 3, md: 4 },
                    borderRadius: 5,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '2rem',
                      color: 'primary.main',
                      opacity: 0.25,
                      lineHeight: 1,
                      mb: 1,
                    }}
                  >
                    {v.index}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.05rem', md: '1.15rem' },
                      color: 'primary.main',
                    }}
                  >
                    {v.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.75,
                      fontSize: '15px',
                    }}
                  >
                    {v.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 3 — Mission */}
      <Box
        id="about-section-2"
        sx={{
          width: '100%',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            bgcolor: palette.primary.main,
            p: { xs: 4, md: 8 },
            mx: 'auto',
            color: palette.primary.contrastText,
            borderRadius: 5,
          }}
          id="about-mission-section"
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'inherit',
              opacity: 0.8,
              letterSpacing: '.12em',
              fontWeight: 600,
              mb: 2,
            }}
          >
            Our Mission
          </Typography>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: {
                    xs: '1.8rem',
                    md: '2.2rem',
                  },
                  lineHeight: 1.15,
                  color: 'inherit',
                  mb: { xs: 2, md: 0 },
                }}
              >
                To make sure nothing carried in memory dies with the person
                carrying it.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography
                sx={{
                  fontSize: { xs: '17px', md: '19px' },
                  lineHeight: { xs: 1.7, md: 1.8 },
                  fontWeight: 500,
                  opacity: 0.92,
                }}
              >
                We believe every family has a story worth protecting. LegacyTies
                helps African diaspora families hear their own history in their
                grandmother's voice, trace their lineage back to the village,
                and pass it all forward — intact.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '17px', md: '19px' },
                  lineHeight: { xs: 1.7, md: 1.8 },
                  fontWeight: 500,
                  mt: 3,
                  opacity: 0.92,
                }}
              >
                One family at a time, we are building something that has never
                existed before a permanent home for stories the world almost
                lost.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* Section 4 — Team */}
      <Box
        id="about-section-3"
        sx={{
          width: '100%',
          bgcolor: palette.primary.contrastText,
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Typography
            variant="h2"
            sx={{
              color: palette.primary.main,
              fontSize: {
                xs: typography.h4.fontSize,
                md: typography.h3.fontSize,
              },
              mb: 1.5,
            }}
          >
            Our Team
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '17px', md: '20px' },
              lineHeight: 1.8,
              mb: 5,
              maxWidth: '60ch',
              color: 'text.primary',
            }}
          >
            We are a UK-based team who watched our own families lose pieces of
            history that couldn't be recovered. LegacyTies is the tool we wished
            had existed for our grandparents.
          </Typography>
          <Grid container spacing={5}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    py: 3,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Avatar
                    src={member.image}
                    alt={member.name}
                    sx={{ width: 88, height: 88 }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                      {member.name}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {member.role}
                    </Typography>
                    <Link
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                    >
                      LinkedIn profile
                    </Link>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* Section 5 — CTA */}
      <Box
        id="about-section-4"
        sx={{
          width: '100%',
          py: { xs: 8, md: 12 },
          bgcolor: 'primary.main',
          color: palette.primary.contrastText,
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: {
                xs: typography.h4.fontSize,
                md: typography.h2.fontSize,
              },
              mb: 2,
              color: 'inherit',
            }}
          >
            Join Us Today
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '18px', md: '20px' },
              lineHeight: { xs: 1.6, md: 1.8 },
              color: 'inherit',
              opacity: 0.88,
              maxWidth: '52ch',
              mb: 4,
            }}
          >
            Be part of a growing community dedicated to keeping family legacies
            alive. Start building your family&apos;s story today.
          </Typography>
          <CustomButton
            label="Start Before It's Too Late"
            variant="contained"
            color="secondary"
            sx={{
              fontSize: {
                xs: '0.8rem',
                sm: '0.9rem',
                md: typography.body1.fontSize,
              },
              color: palette.primary.main,
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap',
              textAlign: 'center',
              '&:hover': {
                color: 'white',
              },
            }}
            onClick={() => navigate('/app')}
          />
        </Container>
      </Box>
    </Box>
  );
}
